// Add US States constant
export const US_STATES = {
  'AL': 'Alabama',
  'AK': 'Alaska', 
  'AZ': 'Arizona',
  'AR': 'Arkansas',
  'CA': 'California',
  'CO': 'Colorado',
  'CT': 'Connecticut',
  'DE': 'Delaware',
  'DC': 'District of Columbia',
  'FL': 'Florida',
  'GA': 'Georgia',
  'HI': 'Hawaii',
  'ID': 'Idaho',
  'IL': 'Illinois',
  'IN': 'Indiana',
  'IA': 'Iowa',
  'KS': 'Kansas',
  'KY': 'Kentucky',
  'LA': 'Louisiana',
  'ME': 'Maine',
  'MD': 'Maryland',
  'MA': 'Massachusetts',
  'MI': 'Michigan',
  'MN': 'Minnesota',
  'MS': 'Mississippi',
  'MO': 'Missouri',
  'MT': 'Montana',
  'NE': 'Nebraska',
  'NV': 'Nevada',
  'NH': 'New Hampshire',
  'NJ': 'New Jersey',
  'NM': 'New Mexico',
  'NY': 'New York',
  'NC': 'North Carolina',
  'ND': 'North Dakota',
  'OH': 'Ohio',
  'OK': 'Oklahoma',
  'OR': 'Oregon',
  'PA': 'Pennsylvania',
  'RI': 'Rhode Island',
  'SC': 'South Carolina',
  'SD': 'South Dakota',
  'TN': 'Tennessee',
  'TX': 'Texas',
  'UT': 'Utah',
  'VT': 'Vermont',
  'VA': 'Virginia',
  'WA': 'Washington',
  'WV': 'West Virginia',
  'WI': 'Wisconsin',
  'WY': 'Wyoming'
} as const;

// Set-Aside Types
export const SET_ASIDES = {
  '8(a)': {
    type: '8(a)',
    name: '8(a) Business Development',
    description: 'Small disadvantaged businesses participating in the 8(a) Business Development program',
    icon: 'Award',
    color: 'text-purple-600'
  },
  'HUBZone': {
    type: 'HUBZone',
    name: 'HUBZone',
    description: 'Historically Underutilized Business Zone small businesses',
    icon: 'Building2',
    color: 'text-blue-600'
  },
  'SDVOSB': {
    type: 'SDVOSB',
    name: 'Service-Disabled Veteran-Owned Small Business',
    description: 'Owned by service-disabled veterans. Requires SBA VetCert certification (self-certification ended Jan 2024); government-wide goal is 5%.',
    icon: 'Shield',
    color: 'text-red-600'
  },
  'VOSB': {
    type: 'VOSB',
    name: 'Veteran-Owned Small Business',
    description: 'Owned by veterans (not necessarily service-disabled). SBA VetCert certified; set-asides offered primarily by the VA under "Veterans First."',
    icon: 'Flag',
    color: 'text-teal-600'
  },
  'WOSB': {
    type: 'WOSB',
    name: 'Women-Owned Small Business',
    description: 'At least 51% owned and controlled by women. Certification required (MySBA Certifications); 5% government-wide goal.',
    icon: 'Heart',
    color: 'text-pink-600'
  },
  'EDWOSB': {
    type: 'EDWOSB',
    name: 'Economically Disadvantaged Women-Owned Small Business',
    description: 'A WOSB whose owners also meet economic-disadvantage thresholds. Certification required (MySBA Certifications).',
    icon: 'Users',
    color: 'text-indigo-600'
  },
  'SB': {
    type: 'SB',
    name: 'Small Business',
    description: 'General total small business set-aside, per SBA size standards by NAICS.',
    icon: 'Briefcase',
    color: 'text-green-600'
  },
  // NOTE: SDB is a self-certified STATUS used for subcontracting credit / agency goaling,
  // not a stand-alone set-aside competition (the 8(a) program is the set-aside vehicle for
  // disadvantaged firms). Kept for data continuity and labeled accordingly.
  'SDB': {
    type: 'SDB',
    name: 'Small Disadvantaged Business (status)',
    description: 'A self-certified status used for subcontracting credit and agency goaling — not a stand-alone competition. For disadvantaged-business set-asides, see 8(a).',
    icon: 'Star',
    color: 'text-orange-600'
  }
} as const;

// Cost Categories
export const COST_CATEGORIES = [
  'Labor',
  'Materials', 
  'Equipment',
  'Fuel',
  'Lease',
  'Overhead'
] as const;

// Work Types
export const WORK_TYPES = {
  'software_development': {
    name: 'Software Development',
    description: 'Custom software development and implementation services',
    defaultComponents: ['Labor', 'Equipment', 'Overhead']
  },
  'cybersecurity': {
    name: 'Cybersecurity',
    description: 'Security assessment, implementation, and monitoring services',
    defaultComponents: ['Labor', 'Equipment', 'Overhead']
  },
  'cloud_services': {
    name: 'Cloud Services',
    description: 'Cloud infrastructure and platform services',
    defaultComponents: ['Labor', 'Equipment', 'Lease']
  },
  'data_analytics': {
    name: 'Data Analytics',
    description: 'Data analysis and business intelligence services',
    defaultComponents: ['Labor', 'Equipment', 'Overhead']
  },
  'it_consulting': {
    name: 'IT Consulting',
    description: 'Technology consulting and advisory services',
    defaultComponents: ['Labor', 'Overhead']
  },
  'network_infrastructure': {
    name: 'Network Infrastructure',
    description: 'Network design, implementation, and maintenance',
    defaultComponents: ['Labor', 'Equipment', 'Materials']
  },
  'system_integration': {
    name: 'System Integration',
    description: 'Integration of multiple systems and platforms',
    defaultComponents: ['Labor', 'Equipment', 'Overhead']
  },
  'hardware_maintenance': {
    name: 'Hardware Maintenance',
    description: 'Maintenance and repair of computer hardware',
    defaultComponents: ['Labor', 'Materials', 'Equipment']
  },
  'training_services': {
    name: 'Training Services',
    description: 'Technical training and education services',
    defaultComponents: ['Labor', 'Materials', 'Lease']
  },
  'technical_support': {
    name: 'Technical Support',
    description: '24/7 technical support and help desk services',
    defaultComponents: ['Labor', 'Equipment', 'Overhead']
  }
} as const;